import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { type Log, type PatternCard, type PatternMarkValue } from "@shared/schema";
import {
  buildHomePatternTabs,
  resolveHomePatternTab,
  type PatternLens,
} from "@shared/pattern-lenses";
import { formatTypographicQuotes } from "@/lib/typographic-quotes";

function daysOnRecord(logs: Array<{ loggedAt: string | Date }>): number {
  if (!logs.length) return 0;
  const firstEntry = logs.reduce<Date | null>((earliest, log) => {
    const loggedAt = new Date(log.loggedAt);
    return !earliest || loggedAt < earliest ? loggedAt : earliest;
  }, null);
  if (!firstEntry || Number.isNaN(firstEntry.getTime())) return 0;
  const start = new Date(firstEntry);
  start.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(1, Math.floor((today.getTime() - start.getTime()) / 86_400_000) + 1);
}

type EnrichedPatternCard = PatternCard & {
  patternMark?: {
    id: number;
    mark: PatternMarkValue;
    markedAt: string | Date;
    baselineCount?: number | null;
    resolvedAt?: string | Date | null;
    absenceAcknowledgedAt?: string | Date | null;
  } | null;
  recurrence?: {
    sinceCount?: number | null;
    priorCount?: number | null;
    lastAppearedAt?: string | Date | null;
    daysSinceLast?: number | null;
  } | null;
};

type StreakDay = {
  day: string;
  logged: boolean;
  isToday: boolean;
};

type StreakWeek = {
  weekStart: string;
  label: string;
  streak: StreakDay[];
  total: number;
};

function workingDate(value: string | Date, timeZone?: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB", { day: "numeric", month: "long", timeZone });
}

function ParticleField({ fill, count = 8000 }: { fill: number; count?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const points = useMemo(() => {
    const result: Array<{ ph: number; lat: number; f: number; j: number; order: number; band: number }> = [];
    let seed = 3313;
    const random = () => {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    };

    for (let index = 0; index < count; index += 1) {
      const theta = Math.acos(1 - 2 * ((index + 0.5) / count));
      const phase = Math.PI * (1 + Math.sqrt(5)) * index;
      const latitude = Math.PI / 2 - theta;
      const longitude = phase % (Math.PI * 2);
      const field = 0.55 + 0.45 * (
        Math.sin(longitude * 3.1 + latitude * 2.2) * 0.5
        + Math.cos(longitude * 5.7 - latitude * 1.4) * 0.3
        + Math.sin(longitude * 9.3 + latitude * 4.9) * 0.2
      );

      result.push({
        ph: phase,
        lat: latitude,
        f: field,
        j: random(),
        order: random(),
        band: Math.floor(((latitude + Math.PI / 2) / Math.PI) * 7),
      });
    }
    return result;
  }, [count]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    let animationFrame = 0;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * ratio;
      canvas.height = window.innerHeight * ratio;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
    };

    const draw = (now: number) => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const width = window.innerWidth;
      const height = window.innerHeight;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);

      const centerX = width / 2;
      const centerY = height * 0.34;
      const scale = Math.min(width, height) * 0.44;
      const yaw = reducedMotion ? 0 : now * 0.00006;
      const pitch = 0.32;
      const project = (x: number, y: number, z: number) => {
        const rotatedX = x * Math.cos(yaw) - z * Math.sin(yaw);
        let rotatedZ = x * Math.sin(yaw) + z * Math.cos(yaw);
        const rotatedY = y * Math.cos(pitch) - rotatedZ * Math.sin(pitch);
        rotatedZ = y * Math.sin(pitch) + rotatedZ * Math.cos(pitch);
        const perspective = 1 / (2.1 - rotatedZ * 0.5);
        return [centerX + rotatedX * scale * perspective * 1.7, centerY + rotatedY * scale * perspective * 1.7, rotatedZ, perspective] as const;
      };

      context.globalCompositeOperation = "source-over";
      context.strokeStyle = "rgba(223,232,238,0.10)";
      context.lineWidth = 0.6;
      for (const latitude of [-60, -30, 0, 30, 60]) {
        context.beginPath();
        let drawing = false;
        for (let longitude = 0; longitude <= 360; longitude += 6) {
          const lat = latitude * Math.PI / 180;
          const lon = longitude * Math.PI / 180;
          const projected = project(Math.cos(lat) * Math.cos(lon), Math.sin(lat), Math.cos(lat) * Math.sin(lon));
          if (projected[2] < -0.1) {
            drawing = false;
            continue;
          }
          if (!drawing) context.moveTo(projected[0], projected[1]);
          else context.lineTo(projected[0], projected[1]);
          drawing = true;
        }
        context.stroke();
      }

      context.globalCompositeOperation = "lighter";
      for (const point of points) {
        const bandLatitude = (point.band / 6) * Math.PI - Math.PI / 2;
        const latitude = point.lat + (bandLatitude - point.lat) * 0.7;
        const theta = Math.PI / 2 - latitude;
        const radius = 1 + (point.f - 0.5) * 0.62;
        const projected = project(
          Math.sin(theta) * Math.cos(point.ph) * radius,
          Math.cos(theta) * radius,
          Math.sin(theta) * Math.sin(point.ph) * radius,
        );
        if (projected[2] < -0.16) continue;

        const depth = (projected[2] + 1) / 2;
        const rim = Math.pow(1 - Math.abs(projected[2]), 2.6);
        const filled = point.order < Math.min(0.95, fill);
        const cool = [110, 200, 235];
        const warm = [255, 150, 90];
        const bone = [228, 236, 242];
        const endpoint = point.f < 0.5 ? cool : warm;
        const blend = Math.abs(point.f - 0.5) * 2;
        const red = endpoint[0] + (bone[0] - endpoint[0]) * (1 - blend);
        const green = endpoint[1] + (bone[1] - endpoint[1]) * (1 - blend);
        const blue = endpoint[2] + (bone[2] - endpoint[2]) * (1 - blend);
        const gray = (red + green + blue) / 3;
        const color = [red, green, blue].map(channel => gray + (channel - gray) * 1.0);
        let opacity = (0.20 + point.j * 0.42) * (0.26 + depth * 0.82) + rim * 0.26;
        if (!filled) opacity *= 0.16;
        if (opacity < 0.014) continue;

        context.fillStyle = `rgba(${filled ? color[0] : 150},${filled ? color[1] : 165},${filled ? color[2] : 180},${opacity})`;
        const size = Math.max(0.6, (0.7 + point.j * 0.9) * projected[3] * 1.35);
        context.fillRect(projected[0], projected[1], size, size);
      }

      context.globalCompositeOperation = "source-over";
      if (!reducedMotion) animationFrame = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener("resize", resize);
    draw(performance.now());
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", resize);
    };
  }, [fill, points]);

  return <canvas ref={canvasRef} className="live-mirror-canvas" data-testid="particle-canvas" data-canvas-position="fixed-inset-0" aria-hidden="true" />;
}

export default function Home() {
  const [, setLocation] = useLocation();
  const [selectedPatternLens, setSelectedPatternLens] = useState<PatternLens | null>(null);
  const streakScrollerRef = useRef<HTMLDivElement>(null);

  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/me"] });
  const { data: streakData } = useQuery<{ weeks?: StreakWeek[]; streak: StreakDay[]; totalThisWeek: number }>({
    queryKey: ["/api/weekly-streak"],
  });
  const { data: statusData } = useQuery<{ count: number; phase: string }>({
    queryKey: ["/api/status"],
    staleTime: 5 * 60 * 1000,
  });
  const { data: logs = [] } = useQuery<Log[]>({
    queryKey: ["/api/logs"],
    staleTime: 5 * 60 * 1000,
    refetchInterval: query => {
      const data = query.state.data as Log[] | undefined;
      return data?.some(log => (
        log.directAddressed === false
        && Boolean(log.summary || log.reflectionQuote || log.takeaways?.length || log.solutionsDiscussed?.length)
      )) ? 1500 : false;
    },
  });
  const { data: patternCards = [] } = useQuery<EnrichedPatternCard[]>({
    queryKey: ["/api/patterns"],
    staleTime: 5 * 60 * 1000,
    refetchInterval: query => {
      const data = query.state.data as EnrichedPatternCard[] | undefined;
      return data?.some(card => card.directAddressed === false) ? 1500 : false;
    },
  });

  const fallbackStreak = ["M", "T", "W", "T", "F", "S", "S"].map(day => ({ day, logged: false, isToday: false }));
  const streakWeeks = streakData?.weeks?.length
    ? streakData.weeks
    : [{
        weekStart: "current",
        label: "THIS WEEK",
        streak: streakData?.streak?.length ? streakData.streak : fallbackStreak,
        total: streakData?.totalThisWeek ?? 0,
      }];
  const displayName = user?.displayName || user?.email?.split("@")[0] || "YOU";
  const timeZone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const entryCount = statusData?.count ?? 0;
  const recordDays = daysOnRecord(logs);
  const particleFill = Math.min(0.95, entryCount / 400);
  const activePatternCards = useMemo(
    () => patternCards.filter(card => !card.patternMark?.resolvedAt),
    [patternCards],
  );
  const populatedPatterns = useMemo(() => buildHomePatternTabs(activePatternCards), [activePatternCards]);
  const activePatternTab = resolveHomePatternTab(populatedPatterns, selectedPatternLens);
  const activePattern = activePatternTab?.card ?? null;
  const savedPattern = activePatternCards[0] ?? null;
  const patternsUnlocked = entryCount >= 7;
  const workingOn = useMemo(() => patternCards
    .filter(card => card.patternMark?.mark === "change" && !card.patternMark.resolvedAt)
    .sort((a, b) => new Date(b.patternMark!.markedAt).getTime() - new Date(a.patternMark!.markedAt).getTime())
    .slice(0, 3), [patternCards]);

  useEffect(() => {
    const nextLens = activePatternTab?.lens ?? null;
    if (selectedPatternLens !== nextLens) {
      setSelectedPatternLens(nextLens);
    }
  }, [activePatternTab?.lens, selectedPatternLens]);

  useEffect(() => {
    const scroller = streakScrollerRef.current;
    if (!scroller || streakWeeks.length < 2) return;
    const frame = requestAnimationFrame(() => {
      scroller.scrollLeft = scroller.scrollWidth;
    });
    return () => cancelAnimationFrame(frame);
  }, [streakWeeks.length]);

  const handlePatternTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ) => {
    let nextIndex: number | null = null;

    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % populatedPatterns.length;
    if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + populatedPatterns.length) % populatedPatterns.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = populatedPatterns.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    const nextTab = populatedPatterns[nextIndex];
    setSelectedPatternLens(nextTab.lens);
    const tabButtons = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    tabButtons?.[nextIndex]?.focus();
  };

  return (
    <main className="live-mirror-home" data-testid="home-page">
      <ParticleField fill={particleFill} />
      <div className="home-particle-scrim" />

      <div className="live-home-content">
        <header className="live-telemetry">
          <div>
            <strong>FRACTAL · <em>LIVE</em></strong>
            <span>{entryCount} {entryCount === 1 ? "ENTRY" : "ENTRIES"} · {recordDays} DAYS ON RECORD</span>
          </div>
          <div className="live-telemetry-right">
            <strong>{new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit" }).toUpperCase()}</strong>
            <span>{new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} LOCAL</span>
          </div>
        </header>

        <section className="live-welcome">
          <div className="live-eyebrow">WELCOME BACK</div>
          <h1 data-testid="text-display-name">{displayName.toUpperCase()}</h1>
        </section>

        <section className="live-week" data-testid="weekly-streak">
          <div
            ref={streakScrollerRef}
            className="live-week-scroller"
            aria-label="Weekly activity history. Swipe left or right to view other weeks."
            data-testid="weekly-streak-scroller"
          >
            {streakWeeks.map(week => (
              <div className="live-week-panel" key={week.weekStart}>
                <div className="live-week-header">
                  <span>{week.label}</span>
                  <span>{week.total} OF 7</span>
                </div>
                <div className="live-ticks" role="list" aria-label={`${week.label.toLowerCase()} activity`}>
                  {week.streak.map((day, index) => (
                    <div
                      key={`${day.day}-${index}`}
                      className={`${day.logged ? "logged" : "empty"} ${day.isToday ? "today" : ""}`.trim()}
                      role="listitem"
                      aria-current={day.isToday ? "date" : undefined}
                      aria-label={`${day.day}: ${day.isToday ? `today, ${day.logged ? "logged" : "not logged"}` : day.logged ? "logged" : "not logged"}`}
                    >
                      <i aria-hidden="true" />
                      <span aria-hidden="true">{day.day.charAt(0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="live-talk">
          <button className="live-talk-link" onClick={() => setLocation("/chat")} data-testid="button-home-chat">
            <span>Let&apos;s talk</span>
            <svg className="live-talk-arrow" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 12 H19 M13 6 L19 12 L13 18" />
            </svg>
          </button>
          <span className="live-talk-rule" aria-hidden="true" />
        </section>

        <section className="live-insight">
          {patternsUnlocked && activePattern ? (
            <>
              <div className="live-insight-tabs" role="tablist" aria-label="Pattern lenses">
                {populatedPatterns.map(({ lens, label }, index) => (
                  <button
                    key={lens}
                    id={`home-pattern-tab-${lens}`}
                    type="button"
                    role="tab"
                    aria-controls="home-pattern-panel"
                    aria-selected={lens === activePatternTab?.lens}
                    tabIndex={lens === activePatternTab?.lens ? 0 : -1}
                    className={lens === activePatternTab?.lens ? "active" : ""}
                    onClick={() => setSelectedPatternLens(lens)}
                    onKeyDown={event => handlePatternTabKeyDown(event, index)}
                    data-testid={`pattern-tab-${lens}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div
                id="home-pattern-panel"
                className="live-insight-body"
                role="tabpanel"
                aria-labelledby={`home-pattern-tab-${activePatternTab?.lens}`}
              >
                <button className="live-insight-message" onClick={() => setLocation("/patterns")} data-testid={`pattern-card-${activePattern.id}`}>
                  {formatTypographicQuotes(activePattern.headline)}
                </button>
                <p className="live-insight-observation">{formatTypographicQuotes(activePattern.insight)}</p>
                <small>
                  <span>CONFIDENCE {activePattern.confidence}%{activePattern.timespan ? ` · ${formatTypographicQuotes(activePattern.timespan).toUpperCase()}` : ""}</span>
                  <button onClick={() => setLocation("/patterns")} data-testid="button-view-patterns">VIEW PATTERNS ↗</button>
                </small>
              </div>
            </>
          ) : patternsUnlocked && savedPattern ? (
            <div className="live-insight-body" data-testid="saved-pattern-summary">
              <span className="live-eyebrow">YOUR SAVED PATTERNS</span>
              <button
                className="live-insight-message"
                onClick={() => setLocation("/patterns")}
                data-testid={`saved-pattern-${savedPattern.id}`}
              >
                {formatTypographicQuotes(savedPattern.headline)}
              </button>
              <p className="live-insight-observation">
                Your pattern history is still here. Your evidence-backed Home lenses will appear as new patterns are verified.
              </p>
              <small>
                <span>{patternCards.length} SAVED {patternCards.length === 1 ? "PATTERN" : "PATTERNS"}</span>
                <button onClick={() => setLocation("/patterns")} data-testid="button-view-saved-patterns">
                  VIEW YOUR PATTERNS ↗
                </button>
              </small>
            </div>
          ) : (
            <div className="live-locked">
              <span className="live-eyebrow">FIRST PATTERN SOON</span>
              <p>Keep leaving a little of yourself here.</p>
              <div className="live-rail"><i style={{ width: `${Math.max(9, Math.min(100, (entryCount / 7) * 100))}%` }} /></div>
              <small>{Math.min(100, Math.round((entryCount / 7) * 100))}% toward the first pattern</small>
            </div>
          )}
        </section>

        {workingOn.length > 0 && (
          <section className="live-working" aria-labelledby="live-working-title">
            <h2 id="live-working-title">WHAT YOU&apos;RE WORKING ON</h2>
            <div className="live-working-list">
              {workingOn.map(card => {
                const since = Number(card.recurrence?.sinceCount ?? 0);
                const prior = Number(card.recurrence?.priorCount ?? 0);
                const comparison = since > prior
                  ? `up from ${prior}`
                  : since < prior
                    ? `down from ${prior}`
                    : `the same as ${prior}`;
                return (
                  <button key={card.id} className="live-working-row" onClick={() => setLocation(`/patterns?pattern=${card.id}`)} data-testid={`button-working-pattern-${card.id}`}>
                    <span>{formatTypographicQuotes(card.insight)}</span>
                    <small>Marked {workingDate(card.patternMark!.markedAt, timeZone)} · <b className="working-count">{since}</b> since, {comparison}</small>
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </div>
      <style>{homeStyles}</style>
    </main>
  );
}

const homeStyles = `
  .live-mirror-home{--mirror-bg:#0B0A09;--mirror-paper:#E4ECF2;--mirror-muted:#A6B3BC;--mirror-faint:#8B99A3;--mirror-line:rgba(193,211,221,.18);--mirror-accent:var(--accent-color);--s2:16px;--s3:20px;--s4:28px;--s5:60px;position:relative;flex:0 0 auto;width:100%;min-height:100dvh;overflow-x:hidden;background:linear-gradient(145deg,#12110F 0%,#0B0A09 48%,#090909 100%);color:var(--mirror-paper);font-family:var(--font-mono)}
  .live-mirror-home *{box-sizing:border-box}
  .live-mirror-canvas{position:fixed;inset:0;z-index:0;pointer-events:none}
  .home-particle-scrim{position:fixed;left:0;right:0;bottom:0;height:58%;z-index:2;pointer-events:none;background:linear-gradient(to top,#0B0A09 6%,rgba(11,10,9,.88) 32%,rgba(11,10,9,.45) 64%,transparent)}
  .live-home-content{position:relative;z-index:4;width:min(100% - 40px,560px);min-height:100dvh;margin:auto;padding:var(--s3) 0 var(--s5);display:flex;flex-direction:column}
  .live-telemetry{display:flex;justify-content:space-between;gap:16px;color:var(--mirror-muted);font:500 .75rem/1.7 var(--font-mono);letter-spacing:.1em}
  .live-telemetry strong,.live-telemetry span{display:block;font-weight:500}.live-telemetry strong{color:var(--mirror-paper)}.live-telemetry em{color:var(--mirror-accent);font-style:normal}.live-telemetry-right{text-align:right}
  .live-welcome{margin-top:var(--s5);text-align:center;display:flex;flex-direction:column;align-items:center;gap:var(--s2)}.live-eyebrow{color:var(--mirror-muted);font:500 .75rem var(--font-mono);letter-spacing:.14em}
  .live-welcome h1{max-width:100%;margin:0;color:var(--mirror-paper);font:700 clamp(2.25rem,11vw,3rem)/1.02 var(--font-display);letter-spacing:.035em;overflow-wrap:anywhere}
  .live-week{width:100%;margin-top:var(--s4);overflow:hidden}.live-week-scroller{display:flex;width:100%;overflow-x:auto;overflow-y:hidden;scroll-snap-type:x mandatory;overscroll-behavior-x:contain;-webkit-overflow-scrolling:touch;touch-action:pan-x;scrollbar-width:none}.live-week-scroller::-webkit-scrollbar{display:none}.live-week-panel{flex:0 0 100%;width:100%;scroll-snap-align:start;scroll-snap-stop:always}.live-week-header{display:flex;align-items:center;justify-content:space-between;color:var(--mirror-muted);font:500 .75rem/1 var(--font-mono);letter-spacing:.14em}.live-ticks{display:flex;align-items:flex-start;justify-content:space-between;width:100%;margin-top:14px}.live-ticks>div{min-width:7px;text-align:center}.live-ticks i{display:block;width:3px;height:26px;margin:auto;background:rgba(223,232,238,.15);box-shadow:none}.live-ticks .logged i{background:var(--week-tick-color);opacity:.70;box-shadow:0 0 8px var(--week-tick-glow)}.live-ticks .today i{height:30px;background:#FFFFFF;opacity:1;box-shadow:0 0 10px rgba(255,255,255,.5)}.live-ticks span{display:block;margin-top:11px;color:rgba(223,232,238,.6);font:400 .75rem/1 var(--font-mono)}.live-ticks .today span{color:#FFFFFF}
  .live-talk{margin-top:var(--s5);display:flex;flex-direction:column;align-items:center;gap:var(--s2)}.live-talk-link{min-height:44px;display:inline-flex;align-items:center;padding:0;border:0;background:none;color:#F2F4F6;font:400 29px/1 var(--font-reflective);cursor:pointer;transition:transform .16s ease}.live-talk-link:active{transform:scale(.985)}.live-talk-arrow{width:20px;height:20px;margin-left:13px;overflow:visible;color:var(--mirror-accent);opacity:1;transform:scale(1.08);transform-origin:center;animation:live-talk-arrow-pulse 3.6s ease-in-out infinite}.live-talk-arrow path{fill:none;stroke:currentColor;stroke-width:2.1;stroke-linecap:round;stroke-linejoin:round}.live-talk-rule{display:block;width:62%;height:1px;opacity:1;background:linear-gradient(90deg,transparent,var(--mirror-accent),transparent);filter:drop-shadow(0 0 10px var(--accent-color-soft));animation:live-talk-rule-pulse 5s ease-in-out infinite}@keyframes live-talk-arrow-pulse{0%,100%{opacity:.5;transform:scale(.94)}50%{opacity:1;transform:scale(1.08)}}@keyframes live-talk-rule-pulse{0%,100%{width:40%;opacity:.35}50%{width:62%;opacity:1}}
  .live-insight{margin-top:var(--s5)}.live-insight-tabs{display:flex;gap:var(--s3);overflow:auto;border-bottom:1px solid var(--mirror-line)}.live-insight-tabs button{min-width:44px;min-height:44px;padding:0;border:0;border-bottom:1px solid transparent;background:none;color:var(--mirror-accent);font:500 .6875rem var(--font-mono);letter-spacing:.12em;text-transform:uppercase;white-space:nowrap;cursor:pointer}.live-insight-tabs button.active{border-bottom-color:var(--mirror-accent)}.live-insight-body{padding-top:var(--s3)}.live-insight-message{display:block;width:100%;min-height:44px;margin:0;padding:0;border:0;background:none;color:var(--mirror-paper);font:400 1.1875rem/1.35 var(--font-reflective);text-align:left;cursor:pointer}.live-insight-observation{max-width:430px;margin:var(--s2) 0 0;color:var(--mirror-muted);font:400 .9375rem/1.48 var(--font-reflective)}.live-insight-body small{display:block;margin-top:var(--s3);color:var(--mirror-faint);font:500 .8125rem/1.45 var(--font-mono);letter-spacing:.08em}.live-insight-body small span{display:block}.live-insight-body small button{display:block;min-width:44px;min-height:44px;margin-top:4px;padding:0;border:0;background:none;color:var(--mirror-accent);font:inherit;letter-spacing:inherit;white-space:nowrap;cursor:pointer}
  .live-locked{padding-top:16px}.live-locked p{margin:12px 0 16px;color:var(--mirror-paper);font:400 1.125rem/1.35 var(--font-reflective)}.live-locked small{color:var(--mirror-faint);font:500 .75rem var(--font-mono);letter-spacing:.08em}.live-rail{height:1px;margin-bottom:12px;background:var(--mirror-line)}.live-rail i{display:block;height:1px;background:var(--mirror-accent)}
  .live-working{margin-top:var(--s5)}.live-working h2{margin:0 0 var(--s2);color:var(--mirror-muted);font:500 .75rem/1.2 var(--font-mono);letter-spacing:.14em}.live-working-list{border-top:1px solid var(--mirror-line)}.live-working-row{width:100%;min-height:60px;display:block;padding:var(--s3) 0;border:0;border-bottom:1px solid var(--mirror-line);background:none;color:var(--mirror-paper);cursor:pointer;text-align:left}.live-working-row span{display:block;font:400 1.125rem/1.3 var(--font-reflective)}.live-working-row small{display:block;margin-top:8px;color:var(--mirror-muted);font:500 .6875rem/1.4 var(--font-mono);letter-spacing:.04em}.live-working-row .working-count{color:var(--mirror-accent);font:inherit}
  .live-talk-link:focus-visible,.live-insight button:focus-visible,.live-working button:focus-visible{outline:1px solid var(--mirror-accent);outline-offset:4px}
  @media(max-width:420px){.live-home-content{width:calc(100% - 36px)}.live-telemetry{font-size:.6875rem}.live-insight-tabs{gap:var(--s2)}.live-working-row{min-height:58px}}
  @media(prefers-reduced-motion:reduce){.live-mirror-home *, .live-mirror-home *::before, .live-mirror-home *::after{animation:none!important;transition:none!important}.live-talk-arrow{opacity:1;transform:scale(1.08)}.live-talk-rule{width:62%;opacity:1}}
`;